import { memo, useCallback, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { STYLE_PRESETS_MAIN, STYLE_PRESETS_MORE } from '../../../data/stylePresets';
import { API } from '../constants';

// 8 curated hero styles — visually distinct, covers the full spectrum
const CURATED_STYLES = [
  {
    label: 'Quiet Luxury',
    prompt: 'Quiet luxury studio aesthetic — ivory and charcoal palette, refined serif typography, extreme whitespace, whisper-soft UI, understated elegance that screams money without trying, boutique gallery pacing',
    sub: 'Ivory & charcoal',
    gradient: 'linear-gradient(135deg, #F5F0EB 0%, #E8E3DC 40%, #8B8682 100%)',
    accent: '#2A2725',
  },
  {
    label: 'Cinematic Dark',
    prompt: 'Cinematic dark moody aesthetic — dramatic deep blacks, gold and amber accents, film grain overlay, letterboxing bars, spotlight radial-gradient, noir atmosphere, high contrast, bold and luxurious',
    sub: 'Black & gold',
    gradient: 'linear-gradient(135deg, #0F0E0D 0%, #1A1816 50%, #2A2420 100%)',
    accent: '#C9A84C',
  },
  {
    label: 'Editorial',
    prompt: 'New York City editorial gallery — strict black and white grid layout, Vogue-style massive serif headings, fashion-magazine asymmetry, gallery opening energy, zero decoration, typography IS the design',
    sub: 'Black & white',
    gradient: 'linear-gradient(135deg, #FAFAFA 0%, #E8E8E8 50%, #1A1A1A 100%)',
    accent: '#B89B55',
  },
  {
    label: 'Light & Airy',
    prompt: 'English garden at golden hour — soft pastels, botanical illustration accents, rounded serif headings, dappled sunlight overlay, wildflower colors, breezy and inviting, light and airy aesthetic',
    sub: 'Soft pastels',
    gradient: 'linear-gradient(135deg, #FDF8F3 0%, #F5E6D8 40%, #E8D5C4 100%)',
    accent: '#C4A095',
  },
  {
    label: 'Film & Analog',
    prompt: '35mm film wedding journal — warm Kodak Portra grain, contact sheet grid layouts, candid unposed energy, handwritten date stamps, analog warmth, like flipping through a roll of developed film',
    sub: 'Warm Portra tones',
    gradient: 'linear-gradient(135deg, #F0ECE4 0%, #D4C4A8 40%, #9B8B6B 100%)',
    accent: '#8B7355',
  },
  {
    label: 'Desert Boho',
    prompt: 'Joshua Tree desert sunset — burnt orange, dusty rose, macrame textures, hand-drawn type, free-spirited asymmetric layout, boho dream energy, warm desert light, terracotta and sage',
    sub: 'Terracotta & sage',
    gradient: 'linear-gradient(135deg, #F8F3ED 0%, #D4A88C 40%, #7A8B6F 100%)',
    accent: '#C17A50',
  },
  {
    label: 'Modern Minimal',
    prompt: 'Architectural Digest cover — clean geometric lines, black and gold, stark modern sans-serif, mathematical spacing, glass-and-steel precision, gallery-white backgrounds, modern minimal',
    sub: 'Clean & precise',
    gradient: 'linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 50%, #2A2A2A 100%)',
    accent: '#1A1A1A',
  },
  {
    label: 'Golden Hour',
    prompt: 'Golden-hour documentary — warm backlit glow, minimal UI that stays out of the way, story-forward layout, photojournalist authenticity, golden warmth, honest and beautiful, romantic sunset tones',
    sub: 'Warm amber glow',
    gradient: 'linear-gradient(135deg, #FDF6E9 0%, #E8C87A 40%, #C9A84C 100%)',
    accent: '#8B7432',
  },
];

const ALL_PILLS = [...STYLE_PRESETS_MAIN, ...STYLE_PRESETS_MORE];

const StepVision = memo(({ freeformVibe, onVibeChange, onStyleChange, onGoToMedia, enhancing, onEnhance }) => {
  const isDark = typeof window !== 'undefined' && localStorage.getItem('gh_marketing_theme') === 'dark';
  const [showMore, setShowMore] = useState(false);

  const handlePresetClick = useCallback((prompt) => {
    onVibeChange(prompt);
    onStyleChange('freeform');
    onGoToMedia();
  }, [onVibeChange, onStyleChange, onGoToMedia]);

  return (
    <div className="animate-fadeIn" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Fixed top: heading + textarea */}
      <div style={{ flexShrink: 0 }}>
        <p className="text-xs font-sans font-medium tracking-widest uppercase mb-2" style={{ color: '#C9A84C', letterSpacing: '0.2em' }}>
          Step 3 of 3
        </p>
        <h2 className="font-serif text-2xl font-bold mb-1" style={{ color: "inherit" }}>
          Tell Iris your vision
        </h2>
        <p className="font-sans text-sm mb-4" style={{ color: 'inherit', opacity: 0.5 }}>
          Describe a feeling, reference a movie, name an artist — anything that captures the vibe you want.
        </p>

        <textarea
          value={freeformVibe}
          onChange={(e) => { onVibeChange(e.target.value); onStyleChange('freeform'); }}
          placeholder="e.g. Dark and cinematic like Blade Runner with neon accents..."
          className="w-full px-4 py-3 rounded-xl text-sm font-sans border outline-none transition-all focus:border-gh-gold resize-none"
          style={{ background: 'rgba(128,128,128,0.08)', borderColor: freeformVibe ? '#C9A84C' : 'rgba(128,128,128,0.15)', color: 'inherit', minHeight: '72px' }}
          data-testid="demo-freeform-input"
          autoFocus
        />

        <p className="text-xs font-sans font-medium mt-4 mb-3" style={{ color: 'inherit', opacity: 0.5 }}>
          Or pick a starting point:
        </p>
      </div>

      {/* Scrollable area */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, paddingBottom: '8px' }}>
        {/* Curated visual style cards — 2x4 grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
          {CURATED_STYLES.map((s, i) => (
            <button
              key={i}
              onClick={() => handlePresetClick(s.prompt)}
              data-testid={`demo-style-card-${i}`}
              style={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '10px',
                border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
                cursor: 'pointer',
                textAlign: 'left',
                padding: 0,
                background: 'none',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'rgba(201,168,76,0.4)';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Gradient thumbnail */}
              <div style={{
                height: '56px',
                background: s.gradient,
                position: 'relative',
              }}>
                {/* Accent dot */}
                <div style={{
                  position: 'absolute',
                  bottom: '6px',
                  right: '6px',
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: s.accent,
                  border: '1.5px solid rgba(255,255,255,0.5)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
              {/* Label */}
              <div style={{
                padding: '8px 10px',
                background: isDark ? 'rgba(26,25,23,0.9)' : '#fff',
              }}>
                <p style={{
                  fontFamily: 'var(--sans)',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: isDark ? '#f2efe9' : '#2a2725',
                  marginBottom: '1px',
                  lineHeight: 1.3,
                }}>{s.label}</p>
                <p style={{
                  fontFamily: 'var(--sans)',
                  fontSize: '10px',
                  color: isDark ? 'rgba(242,239,233,0.4)' : '#8c8782',
                  lineHeight: 1.3,
                }}>{s.sub}</p>
              </div>
            </button>
          ))}
        </div>

        {/* Show more toggle */}
        <button
          onClick={() => setShowMore(!showMore)}
          data-testid="demo-show-more-styles"
          style={{
            width: '100%',
            padding: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            fontFamily: 'var(--sans)',
            fontSize: '12px',
            fontWeight: 500,
            color: isDark ? 'rgba(200,200,200,0.5)' : '#8c8782',
            background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
            border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'all 0.15s',
            marginBottom: showMore ? '12px' : '0',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(201,168,76,0.3)'; e.currentTarget.style.color = '#C9A84C'; }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
            e.currentTarget.style.color = isDark ? 'rgba(200,200,200,0.5)' : '#8c8782';
          }}
        >
          {showMore ? (
            <><ChevronUp size={14} /> Show fewer styles</>
          ) : (
            <><ChevronDown size={14} /> Browse {ALL_PILLS.length}+ more styles</>
          )}
        </button>

        {/* Expanded pills */}
        {showMore && (
          <div className="flex flex-wrap gap-1.5" style={{ animation: 'fadeIn 0.3s ease' }}>
            {ALL_PILLS.map((s, i) => (
              <button
                key={i}
                onClick={() => handlePresetClick(s.prompt)}
                className="px-2.5 py-1 rounded-full text-xs font-sans transition-all"
                style={{
                  color: isDark ? 'rgba(200,200,200,0.6)' : 'rgba(60,55,50,0.5)',
                  background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                  border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                  cursor: 'pointer', fontSize: '11px', lineHeight: '1.4',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(201,168,76,0.15)'; e.currentTarget.style.color = '#C9A84C'; e.currentTarget.style.borderColor = 'rgba(201,168,76,0.3)'; }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
                  e.currentTarget.style.color = isDark ? 'rgba(200,200,200,0.6)' : 'rgba(60,55,50,0.5)';
                  e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

StepVision.displayName = 'StepVision';
export default StepVision;
